import { apiGET, apiPATCH, apiPOST, apiPUT } from '../utils/adapterFunctions'
import { getWebsiteUrl } from '../utils/getWebsiteUrl'
import { getUpdatedToken } from '../utils/cookies'
import { EnvConfig } from '../config/EnvConfig'

const COMPLETE_SITE_URL = getWebsiteUrl()
const LOGIN_2FA_EMAIL_LINK = `${COMPLETE_SITE_URL}verify2fa/`

export const doLogin = async (username, password, token) => {
  const GAToken = getUpdatedToken('_ga')
  const GA_SESSION_ID = getUpdatedToken(EnvConfig.GATSBY_GA_COOKIE)

  return await apiPOST('/auth/login', {
    username,
    password,
    token,
    emailRedirectionLink: LOGIN_2FA_EMAIL_LINK,
    gaCookieSessionValue: GA_SESSION_ID,
    gaCookieValue: GAToken,
  })
}

export const doSocialLogin = async ({ code, type, isIdToken }) => {
  const GAToken = getUpdatedToken('_ga')
  const GA_SESSION_ID = getUpdatedToken(EnvConfig.GATSBY_GA_COOKIE)

  return await apiPOST('/auth/social/sign-in', {
    code,
    type,
    isIdToken,
    gaCookieSessionValue: GA_SESSION_ID,
    gaCookieValue: GAToken,
  })
}

export const doLogout = async (reason = '') => {
  return await apiPOST('/auth/logout', reason, { hasNoData: true })
}

export const getWallet = async (options = {}) => {
  return await apiGET('/wallet', null, options)
}

export const doForgotPassword = async (email, returnUrl, token) => {
  return await apiPOST(
    '/auth/password/forgot/',
    {
      email,
      password_reset_url: returnUrl,
      token,
    },
    { hasNoData: true }
  )
}

export const doActivate = async (token) => {
  return await apiPOST(`/auth/activate/${token}/`, null, {
    hasNestedData: false,
  })
}

export const doVerifyEmail = async (token) => {
  return await apiPUT(`/auth/email/verify/${token}/`, null)
}

export const doResetPassword = async (model) => {
  return apiPOST('/auth/password/reset/', model, { hasNoData: true })
}

export const doCpfCheck = async ({
  cpf,
  token = null,
  nationality_country_id,
}) => {
  return apiPOST(
    '/registration/cpf/check',
    {
      cpf,
      token,
      nationality_country_id,
    },
    {
      hasNestedData: false,
      hasValid: true,
    }
  )
}

export const doStartRegistrationCpfCheck = async ({
  cpf,
  token = null,
  nationality_country_id,
}) => {
  return apiPOST(
    '/registration/cpf/check/v3',
    {
      cpf,
      token,
      nationality_country_id,
    },
    {
      hasNestedData: false,
      hasValid: true,
    }
  )
}

export const getRegistrationCpfCheckStatus = async (
  afId,
  nationality_country_id
) => {
  const params = { nationality_country_id }
  return apiGET(
    `/registration/cpf/check/v3/${afId}`,
    { params },
    { hasNestedData: false, hasValid: true }
  )
}

export const doCafIframeCheck = async (onboardingId) => {
  return apiGET(`/documents/${onboardingId}`, null)
}

export const doEmailCheck = async (email) => {
  return apiPOST(
    '/registration/email/check',
    { email },
    { hasNestedData: false, hasValid: true }
  )
}

export const doSendValidationEmail = async ({
  email,
  national_id,
  language_code,
  recaptcha_token,
}) => {
  return apiPOST('/registration/email/send-token', {
    national_id,
    language_code,
    email,
    recaptcha_token,
  })
}

export const doValidateSocialEmail = async ({
  email,
  national_id,
  language_code,
  recaptcha_token,
}) => {
  return apiPOST('/registration/email/mark-verified', {
    national_id,
    language_code,
    email,
    recaptcha_token,
  })
}

export const doValidateEmail = async ({ 
  email, 
  national_id, 
  token,
  recaptcha_token,
}) => {
  return apiPOST('/registration/email/validate-token', {
    national_id,
    token,
    email,
    recaptcha_token,
  })
}

export const doSendLoggedInValidationEmail = async () => {
  return apiPOST('/player/email/send-token', {})
}

export const doLoggedInValidateEmail = async (token) => {
  return apiPOST('/player/email/validate-token', { token })
}

export const doSendValidationSmsOnVerification = (domain_bound) => {
  return apiPOST('/player/mobile-number/send-collection-verification-sms', 
    { domain_bound }
  )
}

export const doValidateSmsOnVerification = (verification_code) => {
  return apiPOST(
    '/player/mobile-number/check-collection-verification-sms-code',
    { verification_code }
  )
}

export const doRegistrationSmsSend = async (body) => {
  return apiPOST('/registration/mobile-number/send-verification-sms', body)
}

export const doRegistrationSmsValidation = async (body) => {
  return apiPOST(
    '/registration/mobile-number/check-verification-sms-code',
    body
  )
}

export const doSendValidationSms = async (mobile_prefix, mobile_number, domain_bound) => {
  return apiPOST('player/mobile-number/send-verification-sms', {
    mobile_prefix,
    mobile_number,
    domain_bound
  })
}

export const doValidateSms = async (
  mobile_prefix,
  mobile_number,
  verification_code,
  national_id,
  user_id
) => {
  return apiPOST('player/mobile-number/check-verification-sms-code', {
    mobile_prefix,
    mobile_number,
    verification_code,
    national_id,
    user_id,
  })
}

export const doLoggedInValidateSms = async (verification_code) => {
  return apiPATCH('/mobile-verification/verify-verification-sms-code', {
    verification_code,
  })
}

export const doRegistration = async (model) => {
  const GAToken = getUpdatedToken('_ga')
  const GA_SESSION_ID = getUpdatedToken(EnvConfig.GATSBY_GA_COOKIE)

  return await apiPOST('/registration/v4', {
    ...model,
    ga_cookie_value: GAToken,
    ga_cookie_session_value: GA_SESSION_ID,
  })
}

export const doRefreshToken = async (refreshToken, accessToken) => {
  return await apiPOST('/auth/refresh-token', {
    refresh_token: refreshToken,
    auth_token: accessToken,
  })
}

export const getCurrentUser = async () => {
  return await apiGET('/user')
}

// export const getUserActiveStatus = async () => {
//   try {
//     await coreApi.get('/session')
//   } catch (err) {
//     console.log(err)
//   }
// }

export const countryCheckTranslations = async () => {
  return await apiGET('/country/check')
}

export const doBirthdayCheck = async (cpf, dateOfBirth) => {
  return await apiPOST('/dob-check', { cpf, dateOfBirth }, { hasValid: true })
}

export const doResendEmail = async (playerEmail, recaptchaToken) => {
  return await apiPOST(`/account-verification/${playerEmail}/send-email`, {
    emailRedirectionLink: LOGIN_2FA_EMAIL_LINK,
    token: recaptchaToken,
  })
}

export const getIs2faValidated = async (playerEmail) => {
  return await apiGET(`/account-verification/${playerEmail}/is-token-validated`)
}

export const doValidate2fa = async (token) => {
  return await apiPOST(`/account-verification/${token}/validate-token`)
}

export const getOnboardingState = async (token) => {
  return await apiGET(`/liveness/${token}`)
}

export const getForgotPasswordOnboarding = async (token) => {
  return await apiGET(`auth/onboarding/${token}`)
}

export const doGenerateNewForgotPasswordOnboarding = 
async (token, recaptchaToken, email) => {
  return await apiPOST(`auth/onboarding/${token}`, 
    { 
      recaptcha_token: recaptchaToken,
      email
    }
  )
}

export const getOnboardingIdAndUrl = async (token) => {
  return await apiGET(`auth/liveness/${token}`)
}

export const doGenerateOnboarding = async (livenessType, playerId, reason) => {
  return await apiPOST('/liveness', {
    livenessType,
    playerId,
    reason,
  })
}

export const getMigrateableStatus = async ({ email, recaptchaToken }) => {
  return await apiPOST('/registration/user/is-migrateable', 
    { 
      recaptcha_token: recaptchaToken,
      email
    }
  )
}

export const doAccountReOpenProcess = async (model) => {
  return await apiPOST('/account-reopen', model)
}
